import type { GuidancePersona, GuidanceSource } from '@bookora/shared';
import { create } from 'zustand';

import { useSessionStore } from '@/auth/token-store';

import { guidanceQueue } from './queue';
import { readArmed, writeArmed } from './storage';
import { ARMED_TTL_MS } from './timing';
import type { InlineHostKey, TourKey, Viewport } from './tours/types';

/**
 * The guidance runtime — not persisted (bar the one armed tour, per tab), and
 * it never calls the API.
 *
 * Components that SHOW guidance own the recording of what they showed: the
 * `TourRunner` for floating/sheet tours (and the one `INTERRUPTED` emitter),
 * the `InlineTourSlot` for the inline tour, the chip for its offer, the
 * welcome for itself, the hub for `HUB_OPENED`, `StruggleWatch` for a help
 * offer it makes. Everything else — the Help menu, a smart-guide card, a deep
 * link — only asks this store for a tour.
 */

export interface ActiveTour {
  /** Changes on every start, so a restart of the same tour is a new run. */
  runId: number;
  key: TourKey;
  source: GuidanceSource;
  startAtStepId?: string;
  /** «ادامه»: the step to resume at (clamped by the runner). */
  resumeIndex?: number;
  returnFocusTo: HTMLElement | null;
  startedAt: number;
}

export interface ArmedTour {
  key: TourKey;
  source: GuidanceSource;
  startAtStepId?: string;
  /** «ادامه» for a tour that had to be armed: the saved step survives the wait. */
  resumeIndex?: number;
  until: number;
}

export type WelcomePhase =
  /** `GET /guidance` has not answered yet (or not for this clinic). */
  | 'unknown'
  /** The welcome or announcement is due and waiting for a quiet moment. */
  | 'pending'
  | 'open'
  /** Nothing first-run will appear in this tab. */
  | 'resolved';

export interface HubFocus {
  section?: string;
  id?: string;
}

export interface StartOptions {
  source: GuidanceSource;
  startAtStepId?: string;
  resumeIndex?: number;
  returnFocusTo?: HTMLElement | null;
}

/**
 * The quiet offer beside a page chip (or inside an inline host). `help` marks
 * a HELP offer (critic C6): the panel noticed someone stuck — different copy,
 * accepted with source `struggle`, optionally at one step.
 */
export interface TourOffer {
  key: TourKey;
  help?: { context: string; startAtStepId?: string };
}

export interface TourState {
  clinicId: string | null;
  active: ActiveTour | null;
  offer: TourOffer | null;
  armed: ArmedTour | null;
  hubOpen: boolean;
  hubFocus: HubFocus | null;
  hubSource: GuidanceSource;
  pendingAfterHubClose: (() => void) | null;
  inlineHost: { key: InlineHostKey; open: boolean; ready: boolean } | null;
  pageReady: Partial<Record<TourKey, boolean>>;
  welcome: WelcomePhase;
  announcementOpen: boolean;
  /**
   * The help-notes dialog («کمک برای همین صفحه»): the section it is about,
   * `''` for the support-only version, `null` when closed.
   */
  helpNotes: string | null;
  /** Where the user is (kept by `GuidanceRoot`; works under any router). */
  pathname: string;
  search: string;
  viewport: Viewport;
  persona: GuidancePersona;
  /** The page visit in which a tour offer appeared: nothing else proactive there. */
  offerShownOnPath: string | null;
  /** The page visit in which a tip card rendered: no tour auto-offer there. */
  proactiveSurfaceOnPath: string | null;

  start: (key: TourKey, opts: StartOptions) => void;
  end: () => void;
  setOffer: (key: TourKey | null, help?: TourOffer['help']) => void;
  /**
   * `ttlMs`: how long it waits (a page tour: ten minutes; a context tour: a
   * working day). `resumeIndex`: «ادامه» — the step it resumes at once started.
   */
  arm: (
    key: TourKey,
    source: GuidanceSource,
    startAtStepId?: string,
    ttlMs?: number,
    resumeIndex?: number,
  ) => void;
  takeArmed: (key: TourKey) => ArmedTour | null;
  /** «لغو» on the armed notice. */
  disarm: () => void;
  setPageReady: (key: TourKey, ready: boolean) => void;
  openHub: (focus?: HubFocus | null, source?: GuidanceSource) => void;
  closeHub: (then?: () => void) => void;
  runAfterHubClose: () => void;
  setInlineHost: (host: TourState['inlineHost']) => void;
  setWelcome: (phase: WelcomePhase) => void;
  setAnnouncementOpen: (open: boolean) => void;
  setHelpNotes: (section: string | null) => void;
  setLocation: (pathname: string, search: string) => void;
  setEnv: (viewport: Viewport, persona: GuidancePersona) => void;
  markProactiveSurface: () => void;
  resetForClinic: (clinicId: string | null) => void;
}

let runSeq = 0;

const RESET = {
  active: null,
  offer: null,
  armed: null,
  hubOpen: false,
  hubFocus: null,
  pendingAfterHubClose: null,
  inlineHost: null,
  pageReady: {},
  welcome: 'unknown' as WelcomePhase,
  announcementOpen: false,
  helpNotes: null,
  offerShownOnPath: null,
  proactiveSurfaceOnPath: null,
};

function restoredArmed(clinicId: string | null): ArmedTour | null {
  const stored = readArmed(clinicId);
  return stored
    ? {
        key: stored.key as TourKey,
        source: stored.source,
        startAtStepId: stored.startAtStepId,
        resumeIndex: stored.resumeIndex,
        until: stored.until,
      }
    : null;
}

const initialClinic = useSessionStore.getState().activeClinicId;

export const useTourStore = create<TourState>()((set, get) => ({
  clinicId: initialClinic,
  ...RESET,
  // A context tour armed before a reload still waits in this tab.
  armed: restoredArmed(initialClinic),
  hubSource: 'help-menu',
  pathname: typeof window === 'undefined' ? '/' : window.location.pathname,
  search: typeof window === 'undefined' ? '' : window.location.search,
  viewport: 'wide',
  persona: 'staff',

  start(key, opts) {
    runSeq += 1;
    const taken = get().armed?.key === key;
    if (taken) writeArmed(null);
    set((s) => ({
      active: {
        runId: runSeq,
        key,
        source: opts.source,
        startAtStepId: opts.startAtStepId,
        resumeIndex: opts.resumeIndex,
        returnFocusTo: opts.returnFocusTo ?? null,
        startedAt: Date.now(),
      },
      offer: null,
      armed: taken ? null : s.armed,
    }));
  },
  end() {
    set({ active: null });
  },
  setOffer(key, help) {
    set((s) => ({
      offer: key ? (help ? { key, help } : { key }) : null,
      offerShownOnPath: key ? s.pathname : s.offerShownOnPath,
    }));
  },
  arm(key, source, startAtStepId, ttlMs = ARMED_TTL_MS, resumeIndex) {
    const until = Date.now() + ttlMs;
    const clinicId = get().clinicId;
    if (clinicId) writeArmed({ clinicId, key, source, startAtStepId, resumeIndex, until });
    set({ armed: { key, source, startAtStepId, resumeIndex, until } });
  },
  takeArmed(key) {
    const armed = get().armed;
    if (!armed || armed.key !== key) return null;
    writeArmed(null);
    set({ armed: null });
    return armed.until > Date.now() ? armed : null;
  },
  disarm() {
    writeArmed(null);
    set({ armed: null });
  },
  setPageReady(key, ready) {
    set((s) => (s.pageReady[key] === ready ? s : { pageReady: { ...s.pageReady, [key]: ready } }));
  },
  openHub(focus = null, source = 'help-menu') {
    set({ hubOpen: true, hubFocus: focus, hubSource: source, offer: null });
  },
  closeHub(then) {
    if (!get().hubOpen) {
      then?.();
      return;
    }
    set({ hubOpen: false, pendingAfterHubClose: then ?? null });
  },
  runAfterHubClose() {
    const then = get().pendingAfterHubClose;
    set({ pendingAfterHubClose: null, hubFocus: null });
    then?.();
  },
  setInlineHost(host) {
    set({ inlineHost: host });
  },
  setWelcome(phase) {
    set((s) => (s.welcome === phase ? s : { welcome: phase }));
  },
  setAnnouncementOpen(open) {
    set({ announcementOpen: open });
  },
  setHelpNotes(section) {
    set((s) => (s.helpNotes === section ? s : { helpNotes: section }));
  },
  setLocation(pathname, search) {
    set((s) => {
      if (s.pathname === pathname && s.search === search) return s;
      if (s.pathname === pathname) return { search };
      // A new page visit: the one-proactive-thing-per-visit marks lapse.
      return {
        pathname,
        search,
        offerShownOnPath: null,
        proactiveSurfaceOnPath: null,
        offer: null,
      };
    });
  },
  setEnv(viewport, persona) {
    set((s) => (s.viewport === viewport && s.persona === persona ? s : { viewport, persona }));
  },
  markProactiveSurface() {
    set((s) => ({ proactiveSurfaceOnPath: s.pathname }));
  },
  resetForClinic(clinicId) {
    // The tab's armed tour belongs to the clinic it was armed in: a switch to
    // another clinic drops it; the session arriving after a reload (null →
    // the clinic) picks it back up.
    const previous = get().clinicId;
    if (previous && previous !== clinicId) writeArmed(null);
    set({ clinicId, ...RESET, armed: restoredArmed(clinicId) });
  },
}));

/*
 * A clinic switch resets the runtime SYNCHRONOUSLY, inside `setSession`,
 * before any effect runs or the switcher navigates: an abandoned tour sends
 * nothing, and no event built under the old clinic rides the new token.
 */
useSessionStore.subscribe((state, prev) => {
  if (state.activeClinicId === prev.activeClinicId) return;
  useTourStore.getState().resetForClinic(state.activeClinicId);
  guidanceQueue.keepOnly(state.activeClinicId);
});

/** True while anything guidance shows is open, or a first-run surface may still appear. */
export function selectBusy(s: TourState): boolean {
  if (s.active || s.offer || s.hubOpen || s.announcementOpen || s.helpNotes !== null) return true;
  if (s.welcome !== 'resolved') return true;
  return s.offerShownOnPath !== null && s.offerShownOnPath === s.pathname;
}
